import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Pricing.css';

type BillingPeriod = 'monthly' | 'halfyear' | 'annual';

interface PlanFeature {
    text: string;
    included: boolean;
    highlight?: boolean;  // true = this row is a key differentiator
}

interface Plan {
    id: string;
    name: string;
    badge?: string;
    monthlyPrice: number;
    description: string;
    features: PlanFeature[];
    ctaText: string;
    ctaStyle: 'ghost' | 'solid';
    popular: boolean;
}

const BILLING_OPTIONS: { id: BillingPeriod; label: string; months: number; discount: number; badge?: string }[] = [
    { id: 'monthly', label: 'Monthly', months: 1, discount: 0 },
    { id: 'halfyear', label: 'Half Year', months: 6, discount: 10, badge: 'Save 10%' },
    { id: 'annual', label: 'Annual', months: 12, discount: 20, badge: 'Save 20%' },
];

const PLANS: Plan[] = [
    {
        id: 'basic',
        name: 'Basic',
        monthlyPrice: 300,
        description: 'Perfect for intimate weddings with all the tools you need.',
        features: [
            { text: '10 GB Cloud Storage', included: true, highlight: true },
            { text: 'Up to 100 Guest Invitations', included: true, highlight: true },
            { text: 'Standard Email Support', included: true, highlight: true },
            { text: 'Guest RSVP Management', included: true },
            { text: 'Budget Tracker', included: true },
            { text: 'Vendor Management', included: true },
            { text: 'Ceremony Scheduling', included: true },
            { text: 'Photo Gallery', included: true },
            { text: 'Task Management', included: true },
            { text: 'AI RSVP Agent', included: true },
            { text: 'Custom Wedding Domain', included: true },
            { text: 'WhatsApp Broadcast Tools', included: true },
        ],
        ctaText: 'Start Free Trial',
        ctaStyle: 'ghost',
        popular: false,
    },
    {
        id: 'silver',
        name: 'Silver',
        badge: 'Most Popular',
        monthlyPrice: 999,
        description: 'More space, more guests, and priority support for your big day.',
        features: [
            { text: '100 GB Cloud Storage', included: true, highlight: true },
            { text: 'Up to 250 Guest Invitations', included: true, highlight: true },
            { text: 'Priority Support', included: true, highlight: true },
            { text: 'Guest RSVP Management', included: true },
            { text: 'Budget Tracker', included: true },
            { text: 'Vendor Management', included: true },
            { text: 'Ceremony Scheduling', included: true },
            { text: 'Photo Gallery', included: true },
            { text: 'Task Management', included: true },
            { text: 'AI RSVP Agent', included: true },
            { text: 'Custom Wedding Domain', included: true },
            { text: 'WhatsApp Broadcast Tools', included: true },
        ],
        ctaText: 'Choose Silver',
        ctaStyle: 'solid',
        popular: true,
    },
    {
        id: 'gold',
        name: 'Gold',
        badge: 'Premium',
        monthlyPrice: 1999,
        description: 'Unlimited everything with a dedicated account manager.',
        features: [
            { text: 'Unlimited Cloud Storage', included: true, highlight: true },
            { text: 'Unlimited Guest Invitations', included: true, highlight: true },
            { text: 'Dedicated Account Manager', included: true, highlight: true },
            { text: 'Guest RSVP Management', included: true },
            { text: 'Budget Tracker', included: true },
            { text: 'Vendor Management', included: true },
            { text: 'Ceremony Scheduling', included: true },
            { text: 'Photo Gallery', included: true },
            { text: 'Task Management', included: true },
            { text: 'AI RSVP Agent', included: true },
            { text: 'Custom Wedding Domain', included: true },
            { text: 'WhatsApp Broadcast Tools', included: true },
        ],
        ctaText: 'Go Gold ✨',
        ctaStyle: 'solid',
        popular: false,
    },
];

const fmt = (n: number) => new Intl.NumberFormat('en-IN').format(Math.round(n));

const Pricing: React.FC = () => {
    const [billing, setBilling] = useState<BillingPeriod>('monthly');

    const option = BILLING_OPTIONS.find(o => o.id === billing)!;

    /** Per-month effective price after discount */
    const effectiveMonthly = (base: number) =>
        base * (1 - option.discount / 100);

    /** Total charged for the period */
    const periodTotal = (base: number) =>
        effectiveMonthly(base) * option.months;

    const periodLabel = billing === 'monthly'
        ? '/mo'
        : billing === 'halfyear'
            ? '/mo  ·  6-mo plan'
            : '/mo  ·  annual plan';

    const billingNote = (base: number) => {
        if (billing === 'monthly') return null;
        const total = periodTotal(base);
        const suffix = billing === 'halfyear' ? '6 months' : 'year';
        return `Billed ₹${fmt(total)} every ${suffix}`;
    };

    return (
        <div className="pricing-page">
            {/* Background */}
            <div className="pricing-bg">
                <div className="pricing-bg-dots" />
                <div className="pricing-bg-radial" />
            </div>

            {/* Header */}
            <div className="pricing-header">
                <div className="pricing-logo-wrapper">
                    <img src="/shadigram-logo-bold.png" alt="Shadi Gram logo" className="pricing-logo" />
                </div>
                <span className="pricing-eyebrow">Simple, Transparent Pricing</span>
                <h1 className="pricing-title">
                    Choose Your <span className="pricing-title-gold">Wedding Plan</span>
                </h1>
                <p className="pricing-subtitle">
                    Plans that grow with your celebration. Cancel anytime.
                </p>

                {/* ── 3-option Segmented Toggle ── */}
                <div className="billing-segmented">
                    {BILLING_OPTIONS.map(opt => (
                        <button
                            key={opt.id}
                            className={`seg-btn ${billing === opt.id ? 'seg-btn--active' : ''}`}
                            onClick={() => setBilling(opt.id)}
                        >
                            {opt.label}
                            {opt.badge && (
                                <span className={`seg-badge ${opt.id === 'annual' ? 'seg-badge--gold' : ''}`}>
                                    {opt.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Plans Grid */}
            <div className="pricing-grid-wrapper">
                <div className="pricing-grid">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.id}
                            className={`plan-card ${plan.popular ? 'plan-card--popular' : ''} ${plan.id === 'gold' ? 'plan-card--gold' : ''}`}
                        >
                            {/* Badge */}
                            {plan.badge && (
                                <div className={`plan-badge ${plan.id === 'gold' ? 'plan-badge--gold' : ''}`}>
                                    {plan.badge}
                                </div>
                            )}

                            {/* Plan name */}
                            <div className="plan-name-row">
                                <span className={`plan-icon plan-icon--${plan.id}`}>
                                    {plan.id === 'gold' ? (
                                        <img src="/shadigram-logo-white.png" alt="Gold Plan" className="plan-name-logo" />
                                    ) : (
                                        plan.id === 'basic' ? '🌸' : '💍'
                                    )}
                                </span>
                                <h2 className="plan-name">{plan.name}</h2>
                            </div>

                            <p className="plan-description">{plan.description}</p>

                            {/* Price */}
                            <div className="plan-price-block">
                                <span className="plan-currency">₹</span>
                                <span className="plan-amount">
                                    {fmt(effectiveMonthly(plan.monthlyPrice))}
                                </span>
                                <span className="plan-period">/mo</span>
                            </div>

                            {/* Discount badge inline */}
                            {option.discount > 0 && (
                                <div className="plan-discount-row">
                                    <span className="plan-original-price">₹{fmt(plan.monthlyPrice)}/mo</span>
                                    <span className="plan-discount-pill">{option.discount}% OFF</span>
                                </div>
                            )}

                            {/* Billing note */}
                            {billingNote(plan.monthlyPrice) && (
                                <p className="plan-yearly-note">{billingNote(plan.monthlyPrice)}</p>
                            )}

                            {/* Divider */}
                            <div className="plan-divider" />

                            {/* Features */}
                            <ul className="plan-features">
                                {plan.features.map((feat, idx) => (
                                    <li key={idx} className={`plan-feature included${feat.highlight ? ' feature--highlight' : ''}`}>
                                        <span className="feature-check">✦</span>
                                        <span className="feature-text">{feat.text}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTA */}
                            <Link
                                to={`/register?plan=${plan.id}`}
                                className={`plan-cta plan-cta--${plan.ctaStyle} plan-cta--${plan.id}`}
                            >
                                {plan.ctaText}
                            </Link>

                            {/* Trust Signal */}
                            <div className="trust-signal">
                                <span className="trust-icon">🔒</span>
                                <span className="trust-text">Secure via UPI / Razorpay</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom Trust Strip */}
            <div className="pricing-trust-strip">
                <div className="trust-item"><span>🔒</span><span>256-bit SSL Encryption</span></div>
                <div className="trust-item"><span>↩️</span><span>7-Day Money-Back Guarantee</span></div>
                <div className="trust-item"><span>📞</span><span>24/7 Dedicated Support</span></div>
                <div className="trust-item"><span>💳</span><span>UPI, Cards &amp; Net Banking</span></div>
            </div>

            {/* ── B2B Agency Plans Teaser ── */}
            <section className="agency-teaser-section" style={{ backgroundColor: '#f8fafc', padding: '4rem 0', marginTop: '2rem' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                        <div style={{ display: 'inline-block', padding: '6px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '1rem', background: '#e2e8f0', color: '#475569' }}>For Event Professionals</div>
                        <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: '2.5rem', color: '#333', marginBottom: '1rem' }}>Scale Your Wedding Business</h2>
                        <p style={{ fontFamily: '"Inter", sans-serif', fontSize: '1.1rem', color: '#666', maxWidth: '600px', margin: '0 auto' }}>Specialized B2B plans for event management agencies. Manage multiple client weddings from a single powerful dashboard.</p>
                    </div>

                    <div className="pricing-teaser-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', maxWidth: '800px', margin: '0 auto' }}>
                        <div className="teaser-card" style={{ background: 'white', borderRadius: '16px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#333', marginBottom: '1rem' }}>🏢 Agency Starter</div>
                            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#1e293b', marginBottom: '0.5rem' }}><span style={{ fontSize: '1.5rem', verticalAlign: 'top', marginRight: '4px' }}>₹</span>Free</div>
                            <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Perfect for new wedding planners</p>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '2rem', flex: 1 }}>
                                <li style={{ marginBottom: '0.75rem', color: '#475569', fontSize: '0.95rem' }}>✦ Manage up to 3 Weddings</li>
                                <li style={{ marginBottom: '0.75rem', color: '#475569', fontSize: '0.95rem' }}>✦ Centralized Dashboard</li>
                                <li style={{ marginBottom: '0.75rem', color: '#475569', fontSize: '0.95rem' }}>✦ Invite Client Admins</li>
                                <li style={{ marginBottom: '0.75rem', color: '#475569', fontSize: '0.95rem' }}>✦ Standard Support</li>
                            </ul>
                            <Link to="/agency/register" style={{ display: 'block', textAlign: 'center', padding: '0.75rem 1.5rem', borderRadius: '8px', border: '2px solid #C5A059', color: '#C5A059', fontWeight: 600, textDecoration: 'none', transition: 'all 0.2s' }}>Partner for Free</Link>
                        </div>

                        <div className="teaser-card teaser-card--popular" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', padding: '2rem', border: '1px solid #334155', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', position: 'relative', transform: 'scale(1.05)' }}>
                            <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #C5A059 0%, #A8844A 100%)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>Professional Choice</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'white', marginBottom: '1rem' }}>🚀 Agency Pro</div>
                            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'white', marginBottom: '0.5rem' }}><span style={{ fontSize: '1.5rem', verticalAlign: 'top', marginRight: '4px' }}>₹</span>4,999<span style={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 500 }}>/mo</span></div>
                            <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '1.5rem' }}>For established event management companies</p>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '2rem', flex: 1 }}>
                                <li style={{ marginBottom: '0.75rem', color: '#cbd5e1', fontSize: '0.95rem' }}>✦ Manage Unlimited Weddings</li>
                                <li style={{ marginBottom: '0.75rem', color: '#cbd5e1', fontSize: '0.95rem' }}>✦ Centralized Dashboard</li>
                                <li style={{ marginBottom: '0.75rem', color: '#cbd5e1', fontSize: '0.95rem' }}>✦ Priority Account Manager</li>
                                <li style={{ marginBottom: '0.75rem', color: '#cbd5e1', fontSize: '0.95rem' }}>✦ Vendor Network Access</li>
                            </ul>
                            <Link to="/agency/register" style={{ display: 'block', textAlign: 'center', padding: '0.75rem 1.5rem', borderRadius: '8px', background: 'linear-gradient(135deg, #C5A059 0%, #A8844A 100%)', color: 'white', fontWeight: 600, textDecoration: 'none', transition: 'all 0.2s', border: 'none' }}>Apply for Pro</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Pricing;
